import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { useUserContext } from './UserContext';

/**
 * Global branch *scope* for the CRM.
 *
 * This is a VIEW FILTER + a default — never the source of truth for GST/tax
 * attribution. Lists, dashboards and reports read `selectedBranchId` and pass
 * it as a `branchId` query param; the purchase flow uses it only to PRE-SELECT
 * the sale branch, which is still resolved from the member's/plan's candidate
 * branches on the server. `null` means "All Branches" (aggregate view).
 *
 * Persisted in localStorage so a refresh keeps the chosen scope, and read
 * synchronously on init so the first request already carries the filter.
 */
interface BranchContextValue {
  /** null = All Branches (no filter). */
  selectedBranchId: string | null;
  setSelectedBranchId: (branchId: string | null) => void;
}

const STORAGE_KEY = 'selectedBranchId';

function read(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

function write(branchId: string | null) {
  try {
    if (branchId) localStorage.setItem(STORAGE_KEY, branchId);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore quota/availability errors */
  }
}

const BranchContext = createContext<BranchContextValue | undefined>(undefined);

export function BranchProvider({ children }: { children: ReactNode }) {
  const [selectedBranchId, setSelectedBranchIdState] = useState<string | null>(() => read());
  const { userContext } = useUserContext();

  const setSelectedBranchId = useCallback((branchId: string | null) => {
    write(branchId);
    setSelectedBranchIdState(branchId);
  }, []);

  // Seed the scope from the user's default branch on first load, but only when
  // nothing is stored yet — an explicit prior choice always wins.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || !userContext) return;
    seededRef.current = true;
    const defaultBranchId = userContext.user?.defaultBranchId;
    if (defaultBranchId && read() === null) {
      setSelectedBranchId(defaultBranchId);
    }
  }, [userContext, setSelectedBranchId]);

  return (
    <BranchContext.Provider value={{ selectedBranchId, setSelectedBranchId }}>
      {children}
    </BranchContext.Provider>
  );
}

export function useBranch(): BranchContextValue {
  const ctx = useContext(BranchContext);
  if (!ctx) throw new Error('useBranch must be used within a BranchProvider');
  return ctx;
}
